
import React from 'react';
import { motion } from 'framer-motion';
import { Gavel, FileText, Shield, Landmark } from 'lucide-react';
import SectionHeader from '@/components/SectionHeader';
import { Separator } from '@/components/ui/separator';
import TechBackground from '@/components/ui/TechBackground';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const LegalServices = () => {
  const legalServices = [
    {
      title: "Startup Formation",
      description: "Comprehensive legal assistance for business entity formation, including LLC, C-Corp, or S-Corp setup with all necessary documentation.",
      icon: <Gavel size={20} />,
      color: "text-[#33C3F0]",
      borderColor: "border-[#33C3F0]/20",
      bgGradient: "bg-gradient-to-r from-[#33C3F0]/10 to-transparent"
    },
    {
      title: "Intellectual Property",
      description: "Protection for your innovations through patents, trademarks, copyrights, and trade secret strategies tailored to your business needs.",
      icon: <FileText size={20} />,
      color: "text-purple-400",
      borderColor: "border-purple-400/20",
      bgGradient: "bg-gradient-to-r from-purple-400/10 to-transparent"
    },
    {
      title: "Contract Review",
      description: "Expert analysis of business agreements, employment contracts, NDAs, and service agreements to ensure your interests are protected.",
      icon: <Shield size={20} />,
      color: "text-indigo-400",
      borderColor: "border-indigo-400/20",
      bgGradient: "bg-gradient-to-r from-indigo-400/10 to-transparent"
    },
    {
      title: "Compliance & Regulations",
      description: "Navigate complex regulatory environments with guidance on industry-specific compliance requirements and risk management strategies.",
      icon: <Landmark size={20} />,
      color: "text-teal-400",
      borderColor: "border-teal-400/20",
      bgGradient: "bg-gradient-to-r from-teal-400/10 to-transparent"
    }
  ];

  return (
    <section id="legal-services" className="py-6 md:py-10 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <SectionHeader
            title="Legal"
            subtitle="Expert legal guidance to protect your business at every stage"
            emoji="⚖️"
            titleColor="text-white"
          />
        </motion.div>

        <div className="mt-6 md:mt-8">
          <TechBackground 
            variant="dark" 
            glow="blue" 
            className="p-5 md:p-6 max-w-5xl mx-auto relative overflow-hidden"
          >
            {/* Tech-style decorative elements */}
            <div className="absolute top-0 right-0 w-full h-24 opacity-10">
              <div className="w-full h-full" style={{ 
                backgroundImage: 'linear-gradient(to right, #33C3F0, transparent)', 
                clipPath: 'polygon(100% 0, 100% 100%, 75% 100%, 85% 0)' 
              }}></div>
            </div>
            
            <div className="absolute bottom-0 left-0 h-16 w-full opacity-5">
              {/* Binary code pattern */}
              <div className="text-[8px] text-white/30 font-mono leading-tight">
                {Array.from({length: 5}).map((_, i) => (
                  <div key={i} className="overflow-hidden whitespace-nowrap">
                    {Array.from({length: 120}).map((_, j) => (
                      <span key={j}>{Math.random() > 0.5 ? '1' : '0'}</span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="hidden md:block">
              {legalServices.map((service, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="mb-4 last:mb-0 relative"
                >
                  <div className={`p-4 rounded-lg ${service.bgGradient} border ${service.borderColor} backdrop-blur-sm`}>
                    <div className="flex items-start gap-4">
                      <div className={`p-2.5 rounded-lg bg-white/10 backdrop-blur-sm border ${service.borderColor} flex-shrink-0 shadow-sm`}>
                        <span className={service.color}>{service.icon}</span>
                      </div>
                      <div>
                        <h3 className={`text-xl font-semibold mb-1 ${service.color} flex items-center gap-2`}>
                          {service.title}
                          <div className="h-1 w-8 rounded-full bg-gradient-to-r from-white/40 to-transparent"></div>
                        </h3>
                        <p className="text-gray-300 text-sm md:text-base">{service.description}</p>
                      </div>
                    </div>
                  </div>
                  
                  {index < legalServices.length - 1 && (
                    <Separator className="my-4 bg-white/10" />
                  )}
                </motion.div>
              ))}
            </div>
            
            {/* Mobile accordion view */}
            <div className="md:hidden">
              <Accordion type="single" collapsible className="w-full">
                {legalServices.map((service, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-white/10">
                    <AccordionTrigger className={`${service.color} flex items-center gap-2 text-lg font-medium`}>
                      <div className={`p-1.5 rounded-md bg-white/10 backdrop-blur-sm border ${service.borderColor} flex-shrink-0 mr-2`}>
                        <span className={service.color}>{service.icon}</span>
                      </div>
                      {service.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-300 pl-9">
                      {service.description}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="mt-8 text-center relative z-10"
            >
              <div className="inline-block relative">
                <a 
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center text-sm font-medium text-[#33C3F0] hover:text-blue-300 transition-colors bg-white/5 backdrop-blur-sm px-4 py-2 rounded-lg border border-[#33C3F0]/20 group"
                >
                  <span>Need legal assistance for your business?</span>
                  <span className="ml-2 group-hover:translate-x-1 transition-transform">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </a>
                <div className="absolute -bottom-1 left-0 h-px w-full bg-gradient-to-r from-transparent via-[#33C3F0]/50 to-transparent"></div>
              </div>
            </motion.div>
            
            {/* Additional tech decorations */}
            <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full bg-[#33C3F0]/5 blur-xl"></div>
            <div className="absolute top-1/2 transform -translate-y-1/2 -left-10 w-20 h-40 rounded-full bg-purple-400/5 blur-xl"></div>
          </TechBackground>
        </div>
      </div>
    </section>
  );
};

export default LegalServices;
